export function previousGaDateRange(from, to) {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  const rangeDays = Math.max(1, Math.round((end - start) / 86400000) + 1);
  const previousEnd = new Date(start);
  previousEnd.setUTCDate(previousEnd.getUTCDate() - 1);
  const previousStart = new Date(previousEnd);
  previousStart.setUTCDate(previousStart.getUTCDate() - rangeDays + 1);
  return {
    from: previousStart.toISOString().slice(0, 10),
    to: previousEnd.toISOString().slice(0, 10),
  };
}

export function buildEventComparisonRows(currentRows, previousRows) {
  const currentByName = new Map((currentRows || []).map((row) => [row.dimension, row]));
  const previousByName = new Map(
    (previousRows || []).map((row) => [row.dimension, Number(row.eventCount) || 0]),
  );
  const eventNames = new Set([...currentByName.keys(), ...previousByName.keys()]);

  return [...eventNames].map((eventName) => {
    const row = currentByName.get(eventName) || { dimension: eventName };
    const current = Number(row.eventCount) || 0;
    const previous = previousByName.get(eventName) || 0;
    const changeRate = previous > 0 ? (current - previous) / previous : current > 0 ? null : 0;
    return {
      ...row,
      eventCount: current,
      keyEvents: Number(row.keyEvents) || 0,
      previousEventCount: previous,
      changeRate,
    };
  });
}

export const EVENT_ANOMALY_MINIMUM_PREVIOUS_COUNT = 20;
export const EVENT_ANOMALY_CHANGE_THRESHOLD = 0.5;

export function summarizeEventAnomalies(
  rows,
  favorites,
  {
    minimumPreviousCount = EVENT_ANOMALY_MINIMUM_PREVIOUS_COUNT,
    changeThreshold = EVENT_ANOMALY_CHANGE_THRESHOLD,
    limit = 5,
  } = {},
) {
  const favoriteSet = new Set(favorites || []);
  const pinnedAlerts = (rows || [])
    .filter((row) => favoriteSet.has(row.dimension))
    .map((row) => {
      if (row.previousEventCount > 0 && row.eventCount === 0) {
        return { ...row, anomalyType: 'stopped', severity: 'critical' };
      }
      if (
        row.previousEventCount >= minimumPreviousCount &&
        row.changeRate != null &&
        row.changeRate <= -changeThreshold
      ) {
        return { ...row, anomalyType: 'decrease', severity: 'warning' };
      }
      return null;
    })
    .filter(Boolean)
    .sort((a, b) => a.changeRate - b.changeRate);

  const pinnedAlertNames = new Set(pinnedAlerts.map((row) => row.dimension));
  const movers = (rows || [])
    .filter(
      (row) =>
        !pinnedAlertNames.has(row.dimension) &&
        row.previousEventCount >= minimumPreviousCount &&
        row.changeRate != null &&
        Math.abs(row.changeRate) >= changeThreshold,
    )
    .map((row) => ({
      ...row,
      anomalyType: row.changeRate < 0 ? 'decrease' : 'increase',
      severity: 'info',
    }))
    .sort((a, b) => Math.abs(b.changeRate) - Math.abs(a.changeRate))
    .slice(0, limit);

  return {
    monitoredCount: favoriteSet.size,
    pinnedAlerts,
    movers,
  };
}

export function toEventTrendRows(rows) {
  return (rows || [])
    .filter((row) => /^\d{4}-\d{2}-\d{2}$/.test(String(row?.dimension || '')))
    .map((row) => ({ ...row, date: row.dimension }));
}

export function filterAndSortEvents(rows, search, sortKey = 'eventCount') {
  const query = String(search || '').trim().toLowerCase();
  return [...(rows || [])]
    .filter((row) => !query || String(row.dimension || '').toLowerCase().includes(query))
    .sort((a, b) => {
      if (sortKey === 'changeRate') {
        const aRate = a.changeRate == null ? Number.NEGATIVE_INFINITY : a.changeRate;
        const bRate = b.changeRate == null ? Number.NEGATIVE_INFINITY : b.changeRate;
        return bRate - aRate;
      }
      return (Number(b[sortKey]) || 0) - (Number(a[sortKey]) || 0);
    });
}

export const EVENT_FAVORITES_STORAGE_KEY = 'fancian.admin.analytics.favoriteEvents';

export function readFavoriteEvents(storage) {
  if (!storage) return [];
  try {
    const value = JSON.parse(storage.getItem(EVENT_FAVORITES_STORAGE_KEY) || '[]');
    if (!Array.isArray(value)) return [];
    return [...new Set(value.filter((item) => typeof item === 'string' && item.trim()))];
  } catch {
    return [];
  }
}

export function writeFavoriteEvents(storage, favorites) {
  if (!storage) return;
  try {
    storage.setItem(EVENT_FAVORITES_STORAGE_KEY, JSON.stringify(favorites || []));
  } catch {
    // Storage can be unavailable in privacy mode; keep the in-memory selection usable.
  }
}

export function toggleFavoriteEvent(favorites, eventName) {
  const current = new Set(favorites || []);
  if (current.has(eventName)) current.delete(eventName);
  else current.add(eventName);
  return [...current];
}

export function prioritizeFavoriteEvents(rows, favorites) {
  const favoriteSet = new Set(favorites || []);
  return [...(rows || [])].sort(
    (a, b) => Number(favoriteSet.has(b.dimension)) - Number(favoriteSet.has(a.dimension)),
  );
}

export function crashHealthTone(rate) {
  if (!Number.isFinite(rate)) return 'unknown';
  if (rate >= 0.995) return 'healthy';
  if (rate >= 0.99) return 'warning';
  return 'critical';
}
